import { useCallback, useEffect, useRef, useState } from 'react';

import { usePageVisibility } from 'react-page-visibility';
import { useSelector } from 'react-redux';

import { ReadOnlyBanner } from '@/components/atoms/ReadOnlyBanner/ReadOnlyBanner';
import useRtmMessages from '@/hooks/rtm/useRtmMessages';
import { useDeskId } from '@/hooks/useDeskId';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectSessionId } from '@/redux/session/selectors';
import { selectUserId } from '@/redux/user/selectors';
import useSegments from '@/segment/useSegments';
import type { Comment } from '@/shared/types/comments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import ChatBox from './MessageArea/ChatBox/ChatBox';
import MessageArea from './MessageArea/MessageArea';
import { useConversationDetail, useConversationMutations } from '../../hooks/useConversations';
import ConversationHeader from '../ConversationHeader/ConversationHeader';
import { RightSidebar } from '../RightSidebar/RightSidebar';
import { NoteBox } from './MessageArea/NoteBox/NoteBox';

export default function Conversation() {
  const deskId = useDeskId();
  const sessionId = useSelector(selectSessionId);
  const isVisible = usePageVisibility();
  const userId = useSelector(selectUserId);
  const nodeRef = useRef(null);
  const { conversation, getStatus } = useConversationDetail({ deskId, sessionId });
  const { updateRead } = useConversationMutations({ deskId, sessionId });

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.WRITE)
  );

  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteToEdit, setNoteToEdit] = useState<Comment | null>(null);

  const handleAddNote = useCallback(() => setIsAddingNote(true), []);
  const handleEditNote = useCallback((comment: Comment) => setNoteToEdit(comment), []);
  const handleCloseNoteBox = useCallback(() => {
    setIsAddingNote(false);
    setNoteToEdit(null);
  }, []);

  useRtmMessages(deskId, sessionId);

  useSegments(PageName.CONVERSATION);

  useEffect(() => {
    const isRelevant = conversation?.assignee_agent_id === userId || !conversation?.state?.active;
    const hasUnreadMessages = conversation?.agent_unread_count > 0;

    if (isRelevant && hasUnreadMessages && isVisible) {
      updateRead();
    }
  }, [
    conversation?.state?.active,
    conversation?.agent_unread_count,
    conversation?.assignee_agent_id,
    isVisible,
    updateRead,
    userId,
  ]);

  // Clear note box when switching conversations
  useEffect(() => {
    setIsAddingNote(false);
    setNoteToEdit(null);
  }, [sessionId]);

  return (
    <>
      <div
        className="flex min-h-0 min-w-96 flex-1 overflow-hidden bg-bg-muted-light transition-[width] duration-300 ease-in-out"
        ref={nodeRef}
      >
        <div className="flex h-full min-h-0 w-full flex-col">
          <ConversationHeader />

          {!canWrite && <ReadOnlyBanner />}
          <div
            data-conversation-column=""
            className={cn('relative flex min-h-0 flex-1 flex-col bg-bg-muted-light', {
              'flex-1': !canWrite,
            })}
          >
            <MessageArea
              isLoading={getStatus === 'pending'}
              onEditNote={handleEditNote}
              onCancelNoteEdit={handleCloseNoteBox}
            />
            {isAddingNote || noteToEdit ? (
              <NoteBox comment={noteToEdit} onClose={handleCloseNoteBox} />
            ) : (
              <ChatBox onAddNote={handleAddNote} />
            )}
          </div>
        </div>
      </div>
      <RightSidebar />
    </>
  );
}
